import { Loader2 } from "lucide-react";

import { DrawerShell, ProvenanceBadge } from "@/components/comms";
import { EVENT_TYPES, type EventType, usePreferences, useUpdatePreferences } from "@/lib/comms";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

/** Long-form labels the mockup's preferences drawer uses (OE_EVENTS). */
const PREF_LABELS: Record<EventType, string> = {
  order_received: "Order received",
  shipment_created: "Shipment created",
  fulfilment_status: "Shipment fulfilment statuses",
  shipped: "Shipped",
  passed_to_carrier: "Passed on to the carrier",
  delivered: "Delivered",
  backorder: "Backorder",
};

interface ToggleProps {
  on: boolean;
  label: string;
  onClick: () => void;
}

function Toggle({ on, label, onClick }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={cn(
        "relative h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors",
        on ? "bg-ok" : "bg-wg-04",
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform",
          on && "translate-x-5",
        )}
      />
    </button>
  );
}

interface PreferencesDrawerProps {
  customerId: string;
  customerName: string;
  open: boolean;
  onClose: () => void;
}

export function PreferencesDrawer({
  customerId,
  customerName,
  open,
  onClose,
}: PreferencesDrawerProps) {
  const prefsQuery = usePreferences(open ? customerId : null);
  const update = useUpdatePreferences(customerId);
  // While the mutation is in flight, render what the user just chose so the
  // toggle flips instantly; invalidation settles it to the server state.
  const prefs = update.isPending ? update.variables : prefsQuery.data;

  const toggle = (event: EventType) => {
    if (!prefs) {
      return;
    }
    const next = { ...prefs, [event]: !prefs[event] };
    logger.debug("[ordering]", "update preference", customerId, event, next[event]);
    update.mutate(next);
  };

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      title="Customer notification preferences"
      sub={
        <span className="flex items-center gap-2">
          {customerName}
          {/* Preferences are a demo-only toggle — there's no real-data path
              for them, so this is always mock (not read from the API). */}
          <ProvenanceBadge provenance="mock" />
        </span>
      }
    >
      <p className="text-wg-06 mb-4 text-[13px] leading-relaxed">
        Choose which order events send this account a proactive email. Turning everything off opts
        the account out. Changes apply to future orders.
      </p>
      {!prefs ? (
        <Loader2 className="text-wg-05 mx-auto mt-8 size-5 animate-spin" />
      ) : (
        <div className="flex flex-col">
          {EVENT_TYPES.map((event) => (
            <div
              key={event}
              className="border-wg-02 flex items-center justify-between gap-4 border-b py-3 last:border-0"
            >
              <span className="text-wg-08 text-[14px]">{PREF_LABELS[event]}</span>
              <Toggle on={prefs[event]} label={PREF_LABELS[event]} onClick={() => toggle(event)} />
            </div>
          ))}
        </div>
      )}
    </DrawerShell>
  );
}
